import { useState, useEffect, useCallback } from 'react';
import { NavigationBar } from '@client/components/layout';
import { TaskCard, NoTaskCard } from '@client/components/features/tasks';
import { Camera } from '@client/components/features/camera';
import { LoadingSpinner } from '@client/components/common';
import { getCurrentTask, skipTask, uploadPhoto } from '@client/lib/api';
import { hapticSuccess } from '@client/lib/haptics';
import type { Task } from '@shared/types';

interface TaskPageProps {
  onPhotoUploaded?: () => void;
  onCameraStateChange?: (isOpen: boolean) => void;
}

export function TaskPage({ onPhotoUploaded, onCameraStateChange }: TaskPageProps) {
  const [task, setTask] = useState<Task | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);

  const fetchCurrentTask = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCurrentTask();
      setTask(result.task);
      setMessage(result.message || null);
    } catch (error) {
      console.error('Error fetching task:', error);
      setMessage('Fehler beim Laden. Bitte nochmal versuchen.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentTask();
  }, [fetchCurrentTask]);

  const handleShowCamera = (show: boolean) => {
    setShowCamera(show);
    onCameraStateChange?.(show);
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      const result = await skipTask();
      setTask(result.newTask);
      setMessage(result.message || null);
    } catch (error) {
      console.error('Error skipping task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCapture = async (blob: Blob, onProgress: (progress: { loaded: number; total: number; percentage: number }) => void) => {
    if (!task) return;

    try {
      const result = await uploadPhoto(blob, task.id, onProgress);
      hapticSuccess();
      setTask(result.newTask);
      handleShowCamera(false);
      onPhotoUploaded?.();
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error; // Re-throw so Camera can handle it
    }
  };

  return (
    <div className="bg-[var(--color-ios-grouped-background)]">
      <NavigationBar title="Foto-Mission" />

      <div className="pt-6 pb-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : task ? (
          <TaskCard
            task={task}
            onComplete={() => handleShowCamera(true)}
            onSkip={handleSkip}
            loading={loading}
          />
        ) : (
          <NoTaskCard message={message || 'Reiche eine neue Fotoidee ein oder schau gleich nochmal vorbei.'} />
        )}
      </div>

      {showCamera && (
        <Camera
          onCapture={handleCapture}
          onCancel={() => handleShowCamera(false)}
        />
      )}
    </div>
  );
}
